import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DocSorterFileSchema } from "@api/generated/types.gen";

export interface DragItem {
  id: string;
  type: "file";
  file: DocSorterFileSchema;
  sourceCategory: string;
}

export interface DropZone {
  id: string;
  category: string;
  accepts: string[];
}

export interface DragState {
  isDragging: boolean;
  dragItem: DragItem | null;
  dragOverZone: string | null;
}

interface AutoScrollOptions {
  scrollContainer: HTMLElement | null;
  scrollSpeed: number;
  scrollThreshold: number;
}

export const useDragAndDrop = (autoScrollOptions?: Partial<AutoScrollOptions>) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragItem: null,
    dragOverZone: null,
  });

  const scrollTimerRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const options = useMemo(() => {
    const defaultOptions: AutoScrollOptions = {
      scrollContainer: null,
      scrollSpeed: 10,
      scrollThreshold: 60,
    };
    return { ...defaultOptions, ...autoScrollOptions };
  }, [autoScrollOptions]);

  useEffect(() => {
    if (autoScrollOptions?.scrollContainer) {
      scrollContainerRef.current = autoScrollOptions.scrollContainer;
    }
  }, [autoScrollOptions?.scrollContainer]);

  useEffect(() => {
    return () => {
      if (scrollTimerRef.current !== null) {
        window.clearInterval(scrollTimerRef.current);
      }
    };
  }, []);

  const handleDragStart = useCallback(
    (file: DocSorterFileSchema, sourceCategory: string) => {
      const dragItem: DragItem = {
        id: file.id,
        type: "file",
        file,
        sourceCategory,
      };

      setDragState({
        isDragging: true,
        dragItem,
        dragOverZone: null,
      });

      return dragItem;
    },
    []
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent, dropZoneCategory: string) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }

      const scrollContainer = scrollContainerRef.current || options.scrollContainer;
      if (scrollContainer) {
        const { clientY } = event;
        const { top, bottom } = scrollContainer.getBoundingClientRect();
        const threshold = options.scrollThreshold;

        if (clientY < top + threshold || clientY > bottom - threshold) {
          if (scrollTimerRef.current === null) {
            scrollTimerRef.current = window.setInterval(() => {
              const container =
                scrollContainerRef.current || options.scrollContainer;
              if (container && clientY < top + threshold) {
                container.scrollTop -= options.scrollSpeed;
              } else if (container && clientY > bottom - threshold) {
                container.scrollTop += options.scrollSpeed;
              }
            }, 16);
          }
        } else {
          if (scrollTimerRef.current !== null) {
            window.clearInterval(scrollTimerRef.current);
            scrollTimerRef.current = null;
          }
        }
      }

      setDragState((prev) => ({
        ...prev,
        dragOverZone: dropZoneCategory,
      }));
    },
    [options.scrollContainer, options.scrollThreshold, options.scrollSpeed]
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent, dropZoneCategory: string) => {
      event.preventDefault();

      setDragState((prev) => ({
        ...prev,
        dragOverZone: dropZoneCategory,
      }));
    },
    []
  );

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragState((prev) => ({
        ...prev,
        dragOverZone: null,
      }));
    }
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent, targetCategory: string) => {
      event.preventDefault();

      if (scrollTimerRef.current !== null) {
        window.clearInterval(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }

      const { dragItem } = dragState;

      if (!dragItem) return null;

      setDragState({
        isDragging: false,
        dragItem: null,
        dragOverZone: null,
      });

      return {
        file: dragItem.file,
        sourceCategory: dragItem.sourceCategory,
        targetCategory,
      };
    },
    [dragState]
  );

  const handleDragEnd = useCallback(() => {
    if (scrollTimerRef.current !== null) {
      window.clearInterval(scrollTimerRef.current);
      scrollTimerRef.current = null;
    }

    setDragState({
      isDragging: false,
      dragItem: null,
      dragOverZone: null,
    });
  }, []);

  const canDropInZone = useCallback(
    (category: string) => {
      if (!dragState.dragItem) return false;
      return dragState.dragItem.sourceCategory !== category;
    },
    [dragState.dragItem]
  );

  return {
    dragState,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    canDropInZone,
    setScrollContainer: (container: HTMLElement | null) => {
      scrollContainerRef.current = container;
    },
  };
};
